"use client";
import React, { useState, useEffect } from "react";
import { Button, Space, Tooltip, Popconfirm, Empty, Tag, message, Badge } from "antd";
import { useTranslation } from "react-i18next";
import {
  SendOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ExperimentOutlined,
  DownloadOutlined,
  ExpandOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import ResultsTableModal from "../ResultsTableModal/ResultsTableModal";
import ModificationHistoryModal from "../ModificationHistoryModal/ModificationHistoryModal";
import useExtraction from "../../hooks/useExtraction";
import { axiosExtraction } from "../../../api/axios";
import "./RecordViewerPanel.css";

export default function RecordViewerPanel({
  title,
  selected,
  onShare,
  onDelete,
  onPrint,
  onAnalyseAI,
  onDownload, // kept in props signature but no longer used for download logic
  patientId = null,
  canUpdate = true,
  canArchive = true,
}) {
  const { t } = useTranslation();

  // === Hook Redux ===
  const {
    updateParameters,
    refreshAllData,
    documentHistory,
    historyLoading,
    getDocumentHistory,
  } = useExtraction(patientId, { autoLoad: false });

  const [loadedHistory, setLoadedHistory] = useState(null);

  // === Normalisation de l'historique ===
  const historySource = loadedHistory ?? documentHistory;
  const historyList = Array.isArray(historySource)
    ? historySource
    : (historySource?.history || []);
  const historyTotal =
    typeof historySource?.total_modifications === "number"
      ? historySource.total_modifications
      : historyList.length;

  // === Local UI state ===
  const [imageError, setImageError] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [parsedData, setParsedData] = useState(null);

  // === Stats basées sur le document sélectionné ===
  const isBilanBiochimique = selected?.type_document === "BILAN_BIOCHIMIQUE";
  const primarySelectedHistory =
    selected?.modification_history || selected?.historique_modifications || [];
  const selectedHistory = primarySelectedHistory.length
    ? primarySelectedHistory
    : (selected?.change_history || []).filter(
        (entry) => entry?.action === "update_parameters"
      );
  const historyCountOnDoc = selectedHistory.length;
  const totalModsOnDoc =
    selectedHistory.reduce(
      (sum, entry) =>
        sum +
        (entry.total_changes ||
          entry.modifications?.length ||
          entry.changes?.length ||
          (entry.changed_fields?.includes("parametres") ? 1 : 0)),
      0
    ) || 0;

  useEffect(() => {
    setLoadedHistory(null);
  }, [selected?._id]);

  // === Fetch file as blob ===
  useEffect(() => {
    // Revoke previous object URL to avoid memory leaks
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setFileBlob(null);
    setImageError(false);

    const originalPath =
      selected?.chemin_fichier || selected?.file_path || selected?.file;

    if (!originalPath) return;

    let cleanPath = originalPath
      .replace(/\\\\/g, "/")
      .replace(/\\/g, "/")
      .replace(/\/\//g, "/");
    cleanPath = cleanPath.replace(/^\/app\/storage\//, "");
    const parts = cleanPath.replace(/^storage\//, "").split("/");

    if (parts.length < 2) return;

    const collection = parts[0];
    const filename = parts.slice(1).join("/");
    const url = `/consultation/${collection}/${encodeURIComponent(filename)}`;

    const fetchFile = async () => {
      try {
        const response = await axiosExtraction.get(url, {
          params: patientId ? { user_id: patientId } : undefined,
          responseType: "blob",
        });
        const blob = response.data;
        setFileBlob(blob);

        const objectUrl = URL.createObjectURL(blob);
        setFileUrl(objectUrl);
        setImageError(false);
      } catch {
        setFileUrl(null);
        setFileBlob(null);
        setImageError(true);
      }
    };

    fetchFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // === Préparation des données d'analyse (BILAN_BIOCHIMIQUE) ===
  useEffect(() => {
    setParsedData(null);
    setShowResultsModal(false);

    if (selected?.type_document === "BILAN_BIOCHIMIQUE") {
      if (selected.parametres && Array.isArray(selected.parametres)) {
        if (import.meta.env.DEV && import.meta.env.VITE_EXTRACTION_DEBUG_COUNTS === "1") {
          const candidate = selected.change_history?.[0]?.request_id;
          const requestId = typeof candidate === "string" &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate)
              ? candidate : null;
          console.debug("extraction_counts", {
            request_id: requestId,
            frontend_loaded_parameters_count: selected.parametres.length,
          });
        }
        setParsedData({
          parametres: selected.parametres,
          document_info: selected.document_info || {},
          patient: selected.patient || {},
          classification: selected.classification || {},
          laboratoire: selected.laboratoire || {},
          interpretations: selected.interpretations || {},
          extraction_metadata: selected.extraction_metadata || {},
        });
      } else if (selected.donnees_extraites) {
        try {
          const data =
            typeof selected.donnees_extraites === "string"
              ? JSON.parse(selected.donnees_extraites)
              : selected.donnees_extraites;
          setParsedData(data);
        } catch {
          setParsedData(null);
        }
      }
    }
  }, [selected]);

  if (!selected) {
    return (
      <div className="rtp-right card">
        <div className="rtp-right-empty">
          <Empty
            description={t("RecordViewer.viewer.emptySelect", "Select a document from the list")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    );
  }

  // === Infos d'entête ===
  const documentDate = new Date(
    selected.date_upload || selected.createdAt || Date.now()
  );
  const day = documentDate.getDate();
  const month = documentDate.toLocaleString("en", { month: "short" });

  const doctorName =
    selected.medecin_nom ||
    selected.doctor_name ||
    t("RecordViewer.viewer.defaults.unknownDoctor", "Unknown Doctor");

  const fileName =
    selected.nom_fichier ||
    t("RecordViewer.viewer.defaults.document", "Document");

  // === File type detection — use fileName, not blob URL ===
  const isImage = fileUrl && /\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/i.test(fileName);
  const isPDF = fileUrl && /\.pdf$/i.test(fileName);

  // === Download directly from blob — NO backend call ===
  const handleDownload = () => {
    if (!fileBlob) {
      message.error(t("RecordViewer.viewer.preview.noFile", "File not loaded yet"));
      return;
    }
    const blobUrl = URL.createObjectURL(fileBlob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  };

  // === Actions UI ===
  const handleOpenFullscreen = () => {
    if (fileUrl) window.open(fileUrl, "_blank");
  };

  const handleShowResults = () => setShowResultsModal(true);
  const handleCloseResults = () => setShowResultsModal(false);

  const handleShowHistory = async () => {
    if (!selected?.collection || !selected?._id) {
      message.error(
        t(
          "RecordViewer.viewer.history.missingInfo",
          "Cannot load history: missing document information"
        )
      );
      return;
    }
    const response = await getDocumentHistory(selected.collection, selected._id);
    setLoadedHistory(response);
    setShowHistoryModal(true);
  };

  const handleCloseHistory = () => setShowHistoryModal(false);

  const handleApproveResults = async (saveData) => {
    const collection =
      selected?.collection ||
      (selected?.type_document === "BILAN_BIOCHIMIQUE"
        ? "bilan"
        : "autre");
    const documentId = selected?._id || selected?.id;

    if (!documentId) {
      message.error(
        t("RecordViewer.viewer.save.missingId", "Cannot save: Document ID is missing")
      );
      return;
    }

    try {
      const result = await updateParameters(
        collection,
        documentId,
        saveData,
        selected?.version,
      );
      if (result) {
        const updatedDocument = result.data;
        if (updatedDocument?.parametres) {
          setParsedData((current) => ({
            ...(current || {}),
            parametres: updatedDocument.parametres,
          }));
        }
        const updatedHistory = await getDocumentHistory(collection, documentId);
        setLoadedHistory(updatedHistory);
        setShowResultsModal(false);
        message.success(
          t("RecordViewer.viewer.save.success", {
            defaultValue: "{{count}} modifications saved!",
            count: saveData.modifications?.length || 0,
          })
        );
        await refreshAllData();
      }
    } catch {
      message.error(t("RecordViewer.viewer.save.failed", "Failed to save modifications"));
    }
  };

  return (
    <div className="rtp-right card">
      <div className="rtp-right-head">
        <div className="rtp-right-h-title">
          <div className="rtp-right-datepill">
            <span className="day">{day}</span>
            <span className="month">{month}</span>
          </div>

          <div className="rtp-right-lines">
            <div className="rtp-right-doctor">{doctorName}</div>
            <div className="rtp-right-docname">{fileName}</div>

            <Space style={{ marginTop: 4 }}>
              <Tag color="blue">{selected.type_document}</Tag>
              {isBilanBiochimique && totalModsOnDoc > 0 && (
                <Tag color="orange" icon={<HistoryOutlined />}>
                  {t("RecordViewer.viewer.history.modsCount", {
                    defaultValue: "{{count}} modification(s)",
                    count: totalModsOnDoc,
                  })}
                </Tag>
              )}
            </Space>
          </div>
        </div>

        <Space>
          {/* HISTORY */}
          {isBilanBiochimique && (
            <Tooltip
              title={
                historyCountOnDoc > 0
                  ? t("RecordViewer.viewer.history.tooltipHas", {
                      defaultValue: "View modification history ({{count}} changes)",
                      count: totalModsOnDoc,
                    })
                  : t("RecordViewer.viewer.history.tooltipEmpty", "No modification history yet")
              }
            >
              <Badge
                count={loadedHistory ? historyTotal : historyCountOnDoc}
                size="small"
                offset={[-5, 5]}
              >
                <Button
                  className="rv-icon"
                  icon={<HistoryOutlined />}
                  onClick={handleShowHistory}
                  disabled={historyLoading}
                  style={{
                    background: historyCountOnDoc > 0 ? "#fff7e6" : undefined,
                    borderColor: historyCountOnDoc > 0 ? "#ffa940" : "#d9d9d9",
                    color: historyCountOnDoc > 0 ? "#fa8c16" : undefined,
                  }}
                />
              </Badge>
            </Tooltip>
          )}

          <Tooltip title={t("RecordViewer.viewer.actions.openNewTab", "Open in new tab")}>
            <Button
              className="rv-icon"
              icon={<ExpandOutlined />}
              onClick={handleOpenFullscreen}
              disabled={!fileUrl}
            />
          </Tooltip>

          {/* ✅ DOWNLOAD — always uses blob, never hits backend */}
          <Tooltip title={t("RecordViewer.viewer.actions.download", "Download")}>
            <Button
              className="rv-icon"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              disabled={!fileBlob}
            />
          </Tooltip>

          <Tooltip title={t("RecordViewer.viewer.actions.share", "Share")}>
            <Button
              className="rv-icon"
              icon={<SendOutlined />}
              onClick={() => onShare?.(selected)}
            />
          </Tooltip>

          {canArchive && onDelete && (
          <Tooltip title={t("RecordViewer.viewer.actions.delete", "Move to trash")}>
            <Popconfirm
              title={t("RecordViewer.viewer.delete.title", "Delete this document?")}
              description={t("RecordViewer.viewer.delete.desc", "This action cannot be undone.")}
              onConfirm={() => {
                if (!onDelete) return;
                onDelete({
                  id: selected._id || selected.id,
                  _id: selected._id,
                  type: selected.type_document,
                  collection: selected.collection,
                  version: selected.version,
                  rawData: selected,
                });
              }}
              okText={t("RecordViewer.viewer.delete.ok", "Yes, delete")}
              cancelText={t("RecordViewer.viewer.delete.cancel", "Cancel")}
              okButtonProps={{ danger: true }}
            >
              <Button className="rv-icon rv-icon--danger" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
          )}
        </Space>
      </div>

      {isBilanBiochimique && parsedData && (
        <div style={{ marginBottom: 12, textAlign: "center" }}>
            <Button
            type="primary"
            icon={<ExperimentOutlined />}
              onClick={handleShowResults}
            style={{
              background: "linear-gradient(135deg, #006eea  0%,  #006eea  100%)",
              border: "none",
              borderRadius: 8,
              height: 40,
              fontWeight: 600,
            }}
            >
            {t("RecordViewer.viewer.analysis.viewResults", {
              defaultValue: "📊 View Analysis Results ({{count}} parameters)",
              count: parsedData?.parametres?.length || 0,
            })}
            </Button>
        </div>
      )}

      <div className="rtp-right-frame">
        {!fileUrl ? (
          <Empty
            description={t("RecordViewer.viewer.preview.noFile", "No file available for preview")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : isImage ? (
          <div className="rtp-image-container">
            {imageError ? (
              <Empty
                description={
                  <div>
                    <p>{t("RecordViewer.viewer.preview.imageFailTitle", "Failed to load image")}</p>
                    <Button
                      type="primary"
                      icon={<ExpandOutlined />}
                      onClick={handleOpenFullscreen}
                      style={{ marginTop: 16 }}
                    >
                      {t("RecordViewer.viewer.preview.tryOpen", "Try opening in new tab")}
                    </Button>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <img
                src={fileUrl}
                alt={fileName}
                className="rtp-image-preview"
                onError={() => setImageError(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  cursor: "pointer",
                }}
                onClick={handleOpenFullscreen}
              />
            )}
          </div>
        ) : isPDF ? (
          <iframe title="record-file" src={fileUrl} className="rtp-iframe" />
        ) : (
          <div className="rtp-unsupported">
            <Empty
              description={
                <div>
                  <p>
                    {t(
                      "RecordViewer.viewer.preview.unsupported",
                      "Preview not available for this file type"
                    )}
                  </p>
                  <p style={{ fontSize: 12, color: "#8c8c8c", marginTop: 8 }}>{fileName}</p>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    disabled={!fileBlob}
                    style={{ marginTop: 16 }}
                  >
                    {t("RecordViewer.viewer.preview.openFile", "Download File")}
                  </Button>
                </div>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>

      <div className="rtp-right-footer">
        <Button
          icon={<PrinterOutlined />}
          onClick={() => onPrint?.(selected)}
          disabled={!fileUrl}
        >
          {t("RecordViewer.viewer.footer.print", "Print Document")}
        </Button>

        <Button
          type="primary"
          icon={<ExperimentOutlined />}
          onClick={() => onAnalyseAI?.(selected)}
        >
          {t("RecordViewer.viewer.footer.ai", "Analyse with AI")}
        </Button>
      </div>

      {showResultsModal && isBilanBiochimique && parsedData && (
      <ResultsTableModal
          visible={showResultsModal}
          result={{ data: parsedData }}
          document={selected}
          onClose={handleCloseResults}
        onSave={canUpdate ? handleApproveResults : undefined}
        readOnly={!canUpdate}
        />
      )}

      {showHistoryModal && (
        <ModificationHistoryModal
          visible={showHistoryModal}
          onClose={handleCloseHistory}
          document={selected}
          history={historyList}
          total={historyTotal}
          loading={historyLoading}
        />
      )}
    </div>
  );
}
